import React, { useState, useRef, FormEvent, ChangeEvent } from 'react';
import PhoneInput, { Value as PhoneInputValue } from 'react-phone-number-input';
import Image from 'next/image';
import CompanyShippingLocationsSelection from '@/components/CompanyShippingLocationsSelection';
import { ICompanyData } from '@/components/Modals/RegisterCompany/View';
import NewPhoneInput from '@/components/PhoneInput';
import { useUser } from '@/contexts/AuthContext';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { ICompany } from '@/types/api/companies';
import { ILocation } from '@/types/api/locations';
import {
  ALLOWED_IMAGE_FILE_TYPES,
  MAXIMUM_IMG_SIZE_ALLOWED,
} from '@/utils/constants';
import { MyErrorResponse } from '@/utils/handlers';
import { companyContactsSchema } from '@/validators/registerCompany';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  Avatar,
  Badge,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { AxiosError } from 'axios';

interface ICompanyContactsProps {
  companyData: ICompanyData;
  onAdvancedStep: () => void;
}

const CompanyContacts = ({
  onAdvancedStep,
  companyData,
}: ICompanyContactsProps) => {
  const { updateUserData, user } = useUser();
  const { setIsLoading } = useLoading();
  const { id } = companyData;

  const companyImageProfileRef = useRef<HTMLInputElement | null>(null);
  const [companyImageProfile, setCompanyImageProfile] = useState<
    File | string | undefined
  >(companyData.logo);
  const [phone, setPhone] = useState(companyData.phone || '');
  const [locationsChecked, setLocationsChecked] = useState<ILocation[]>(
    companyData?.shipping_locations || []
  );

  const generateFormDataPayload = (email: string) => {
    const formDataUpload = new FormData();
    const shipping_locations = locationsChecked.map(({ id }) => id);

    if (typeof companyImageProfile !== 'string') {
      formDataUpload.append('logo', companyImageProfile || '');
    }
    formDataUpload.append('full_name', companyData?.full_name || '');
    formDataUpload.append('phone', phone);
    formDataUpload.append('email', email as string);
    formDataUpload.append('has_confirmed_configuration', String(true));
    formDataUpload.append(
      'has_confirmed_address',
      String(companyData?.has_confirmed_address)
    );

    shipping_locations.forEach(shipping_location => {
      formDataUpload.append('shipping_locations', shipping_location.toString());
    });
    companyData?.operation_types?.forEach(operation_type =>
      formDataUpload.append('operation_types', operation_type)
    );
    companyData?.categories?.forEach(category => {
      formDataUpload.append('categories', String(category.id));
    });
    companyData?.subcategories?.forEach(subcategory =>
      formDataUpload.append('subcategories', String(subcategory.id))
    );
    return formDataUpload;
  };

  const handleChangeCompanyImg = (e: ChangeEvent<HTMLInputElement>) => {
    const imgFile = e.target.files;

    if (!imgFile || imgFile.length === 0) {
      return;
    }

    if (imgFile[0].size >= MAXIMUM_IMG_SIZE_ALLOWED) {
      Toast('error', 'Tamanho máximo de arquivo excedido (30 mb)');
      return;
    }

    if (!ALLOWED_IMAGE_FILE_TYPES.includes(imgFile[0].type)) {
      Toast('error', 'Formato não suportado! (São permitidos: PNG, JPG, JPEG)');
      return;
    }

    setCompanyImageProfile(imgFile[0]);
  };

  const handleSubmitInformContactsForm = async (
    e: FormEvent<HTMLFormElement>
  ) => {
    try {
      e.preventDefault();
      setIsLoading(true);
      const formData = new FormData(e.target as HTMLFormElement);
      const email = formData.get('email');

      await companyContactsSchema.validate({
        email,
        phone,
        shipping_locations: locationsChecked,
      });

      const formDataUpload = generateFormDataPayload(email as string);

      const { data } = await api.patch<ICompany>(
        `/api/companies/${id}/`,
        formDataUpload,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      updateUserData({
        memberships: user.memberships?.map(membership =>
          membership.company.id === companyData.id
            ? {
                ...membership,
                company: { ...membership.company, ...data },
              }
            : membership
        ),
      });
      onAdvancedStep();
    } catch (error) {
      const errorResponseData = (error as AxiosError<MyErrorResponse>).response
        ?.data;
      if (errorResponseData?.phone) {
        Toast('error', `Telefone: ${errorResponseData.phone[0]}`);
        return;
      }
      if (errorResponseData?.email) {
        Toast('error', `Email: ${errorResponseData.email[0]}`);
        return;
      }
      if (errorResponseData?.shipping_locations) {
        Toast(
          'error',
          `Locais de entrega: ${errorResponseData.shipping_locations[0]}`
        );
        return;
      }
      if (error instanceof Error) {
        Toast('error', error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      onSubmit={handleSubmitInformContactsForm}
      maxWidth="421px"
      sx={{
        margin: '0 auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '38px',
      }}
      component="form"
    >
      <Box component="section">
        <Typography
          component="h3"
          variant="h6"
          sx={{ opacity: '50%', marginBottom: '6px' }}
        >
          Informe os dados de contato
        </Typography>
        <Grid container spacing="18px">
          <Grid item xs={12}>
            <PhoneInput
              defaultCountry="BR"
              value={phone as PhoneInputValue}
              onChange={(value: PhoneInputValue) => setPhone(value)}
              initialValueFormat="national"
              placeholder="WhatsApp da empresa"
              name="whatsapp"
              label="Whatsapp"
              limitMaxLength
              inputComponent={NewPhoneInput}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="standard"
              placeholder="E-mail"
              name="email"
              type="email"
              label="E-mail"
              defaultValue={companyData.email}
              inputProps={{
                'data-testid': 'email-input',
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <CompanyShippingLocationsSelection
        selectedLocations={locationsChecked}
        handleChangeLocations={updatedLocations =>
          setLocationsChecked(updatedLocations)
        }
      />

      <Box
        component="section"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginBottom: 'auto',
        }}
      >
        <Typography
          component="h3"
          variant="h6"
          sx={{ opacity: '50%', marginBottom: '16px' }}
        >
          Foto de perfil da empresa
        </Typography>
        <input
          onChange={handleChangeCompanyImg}
          accept={'image/png, image/jpg, image/jpeg'}
          ref={companyImageProfileRef}
          className="sr-only"
          name="company-image-profile"
          type="file"
          placeholder="Adicione uma foto de perfil para sua empresa"
        />
        <Badge
          overlap="circular"
          sx={{ alignSelf: 'center' }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <Avatar
              sx={{
                backgroundColor: '#FFF',
                color: 'text.secondary',
                border: '1px solid currentColor',
              }}
              onClick={() => companyImageProfileRef.current?.click()}
              data-testid="button-change-company-image-profile"
            >
              <IconButton>
                <CameraAltOutlinedIcon />
              </IconButton>
            </Avatar>
          }
        >
          <Avatar
            sx={{
              width: '135px',
              height: '135px',
              backgroundColor: '#E9E9E9',
              position: 'relative',
              color: 'text.secondary',
              border: '1px solid currentColor',
            }}
          >
            {companyImageProfile ? (
              <Image
                alt="Foto de perfil da empresa"
                objectFit="cover"
                layout="fill"
                src={
                  typeof companyImageProfile === 'string'
                    ? companyImageProfile
                    : URL.createObjectURL(companyImageProfile)
                }
              />
            ) : (
              <StorefrontIcon sx={{ color: '#C7C7C7', fontSize: '78px' }} />
            )}
          </Avatar>

          {companyImageProfile && (
            <Avatar
              sx={{
                position: 'absolute',
                right: 5,
                width: '28px',
                height: '28px',
                backgroundColor: '#FFF',
                border: '1px solid rgba(0,0,0,0.5)',
              }}
            >
              <IconButton
                onClick={() => {
                  setCompanyImageProfile(undefined);
                  if (companyImageProfileRef?.current?.files)
                    companyImageProfileRef.current.files = null;
                }}
              >
                <CloseIcon
                  sx={{ fontSize: '18px' }}
                  data-testid="icon-remove-company-image"
                />
              </IconButton>
            </Avatar>
          )}
        </Badge>
      </Box>

      <Button
        type="submit"
        data-testid="button-register-company"
        className="submitButton"
        fullWidth
        variant="contained"
        sx={{
          position: 'relative',
          zIndex: 1,
        }}
      >
        Configurar
      </Button>
    </Box>
  );
};

export type { ICompanyContactsProps };
export default CompanyContacts;
